import debug from 'debug'
import { QRCodeSVG } from 'qrcode.react'
import { type FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'
import { match } from 'ts-pattern'
import MdiLoading from '~icons/mdi/loading'
import { useGetOrderById } from 'src/services/apollo/payment'
import type {
  OpenPaymentDialogReturns,
  PaymentExecuteFn,
} from 'src/services/helpers/payment'
import { PaymentProvider } from 'src/services/helpers/payment'
import { getIsMobileDevice } from 'src/utils/hooks/useIsMobileDevice'
// simple-icons ships only the messenger glyph; swap when "wechatpay" lands.
import SimpleIconsWechat from '~icons/simple-icons/wechat'

const log = debug('pixai:payment:onerway-wechat-pay')

interface PaymentConfigQR {
  mode: 'qr'
  orderId: string
  // Onerway returns either raw text (preferred, render with qrcode.react)
  // or a hosted image URL (fallback). At least one must be present.
  qrCode?: string
  qrCodeUrl?: string
}

interface PaymentDialogContentOnerwayWechatPayProps {
  execute: PaymentExecuteFn
  trackingFnAfterFetch?: () => void
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
}

export const PaymentDialogContentOnerwayWechatPay: FC<
  PaymentDialogContentOnerwayWechatPayProps
> = ({ execute, trackingFnAfterFetch, onSucceeded, onFailed }) => {
  // Lock isMobile at mount time — avoids mode-flip on resize / DevTools toggle.
  const isMobileRef = useRef(getIsMobileDevice())
  const isMobile = isMobileRef.current

  const [paymentConfig, setPaymentConfig] = useState<PaymentConfigQR | null>(
    null,
  )

  const { error } = useAsync(async () => {
    // Mobile shows a desktop-only notice — skip execute() to avoid
    // creating an orphan order that backend never finalizes.
    if (isMobile) return

    const result = await execute({
      provider: PaymentProvider.onerwayWechatPay,
      providerOptions: { paymentMethod: 'wechat_qr' },
    })

    const { orderId, qrCode, qrCodeUrl } = result

    if (!qrCode && !qrCodeUrl) throw new Error('Missing wechat_qr code')
    setPaymentConfig({ mode: 'qr', orderId, qrCode, qrCodeUrl })

    trackingFnAfterFetch?.()
  })

  useEffect(() => {
    if (error) {
      log('Failed to fetch payment configuration', error)
      onFailed?.(error)
    }
  }, [error])

  return (
    <div className="rounded-lg flex items-center justify-center size-full bg-neutral-100">
      {isMobile ? (
        <PaymentDialogContentOnerwayWechatPayDesktopOnly />
      ) : (
        match(paymentConfig)
          .with(null, () => (
            <div className="py-8 size-full text-black/75 flex justify-center text-sm select-none">
              <MdiLoading className="size-6 animate-spin" />
            </div>
          ))
          .with({ mode: 'qr' }, p => (
            <PaymentDialogContentOnerwayWechatPayQRCode
              config={p}
              onSucceeded={() =>
                onSucceeded?.({
                  orderId: p.orderId,
                  finalizedPaymentProvider: PaymentProvider.onerwayWechatPay,
                })
              }
              onFailed={onFailed}
            />
          ))
          .exhaustive()
      )}
    </div>
  )
}

const PaymentDialogContentOnerwayWechatPayDesktopOnly: FC = () => {
  const { t } = useTranslation(['common'])
  return (
    <div
      className="p-8 flex flex-col items-center gap-4 max-w-sm"
      data-testid="wechat-desktop-only-notice"
    >
      {/* WeChat Pay brand color: #07C160 */}
      <SimpleIconsWechat className="w-12 h-12 text-[#07C160]" />
      <div className="flex flex-col items-center gap-1.5">
        <p className="text-base font-semibold text-center text-black">
          {t('common:payment.wechat.desktop-only-title')}
        </p>
        <p className="text-xs text-center text-black/60 leading-relaxed">
          {t('common:payment.wechat.desktop-only-detail')}
        </p>
      </div>
    </div>
  )
}

const PaymentDialogContentOnerwayWechatPayQRCode: FC<{
  config: PaymentConfigQR
  onSucceeded?: () => void
  onFailed?: (error: Error) => void
}> = ({ config, onSucceeded, onFailed }) => {
  // WeChat strings live in `common` (shared with credit-pack flow);
  // payment-failure copies stay in `membership`.
  const { t } = useTranslation(['common', 'membership'])
  const { data } = useGetOrderById(config.orderId)

  useEffect(() => {
    if (data?.status === 'completed') {
      log('Payment completed', data)
      onSucceeded?.()
    } else if (data?.status === 'cancelled') {
      log('Payment cancelled', data)
      onFailed?.(new Error(t('membership:payment.cancelled-due-to-timeout')))
    } else if (data?.status === 'failed') {
      log('Payment failed', data)
      onFailed?.(new Error(t('membership:payment.failed-generic')))
    }
  }, [data?.status])

  return (
    <div className="p-8 flex flex-col items-center gap-4 bg-neutral-100">
      <div
        className="size-32 relative flex items-center justify-center"
        data-testid="wechat-qr-container"
      >
        {config.qrCode ? (
          // Preferred: render the QR ourselves so we can overlay the
          // WeChat logo for brand recognition.
          <>
            <QRCodeSVG value={config.qrCode} />
            <div className="absolute inset-0 flex items-center justify-center">
              {/* WeChat Pay brand color: #07C160 */}
              <SimpleIconsWechat className="w-6 h-6 text-[#07C160] bg-white rounded-sm p-0.5" />
            </div>
          </>
        ) : config.qrCodeUrl ? (
          // Fallback: onerway's hosted QR image. No overlay — we can't
          // guarantee error-correction headroom.
          <img
            src={config.qrCodeUrl}
            alt={t('common:payment.wechat.scan-to-pay')}
            className="size-32"
          />
        ) : null}
      </div>

      <div className="h-px w-full bg-gray-200" />

      <div className="flex items-start text-xs gap-2 opacity-80 mt-4">
        <MdiLoading className="size-4 animate-spin-slow shrink-0" />
        <span>{t('common:payment.wechat.pending-payment-completion')}</span>
      </div>
    </div>
  )
}
